import React, { useState } from 'react'

interface FeedbackViewProps {
  onClose: () => void
  onSubmit: (feedback: { rating: number; comment: string }) => void
  primaryColor?: string
}

export function FeedbackView({ onClose, onSubmit, primaryColor = '#3b82f6' }: FeedbackViewProps) {
  const [rating, setRating] = useState(0)
  const [hoveredRating, setHoveredRating] = useState(0)
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('Please select a rating')
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit({ rating, comment })
      alert('Thank you for your feedback!')
      onClose()
    } catch (error) {
      alert('Failed to submit feedback. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const StarIcon = ({ filled, onHover, onClick }: { filled: boolean; onHover: () => void; onClick: () => void }) => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill={filled ? primaryColor : 'none'}
      stroke={filled ? primaryColor : '#d1d5db'}
      strokeWidth="2"
      style={{ cursor: 'pointer' }}
      onMouseEnter={onHover}
      onClick={onClick}
    >
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  )

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h4 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Rate Your Experience</h4>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px'
          }}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div style={{
        padding: '24px',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        backgroundColor: 'white',
        textAlign: 'center'
      }}>
        {/* Rating Stars */}
        <div style={{ marginBottom: '24px' }}>
          <p style={{ fontSize: '16px', marginBottom: '16px', color: '#374151' }}>
            How would you rate our service?
          </p>
          <div 
            style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}
            onMouseLeave={() => setHoveredRating(0)}
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <StarIcon
                key={star}
                filled={star <= (hoveredRating || rating)}
                onHover={() => setHoveredRating(star)}
                onClick={() => setRating(star)}
              />
            ))}
          </div>
          {rating > 0 && (
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              {rating === 1 && '😞 Poor'}
              {rating === 2 && '😐 Fair'}
              {rating === 3 && '🙂 Good'}
              {rating === 4 && '😊 Very Good'}
              {rating === 5 && '🤩 Excellent'}
            </p>
          )}
        </div>

        {/* Comment */}
        <div style={{ marginBottom: '24px', textAlign: 'left' }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '600', 
            marginBottom: '8px',
            color: '#374151'
          }}>
            Additional Comments (Optional)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Tell us more about your experience..."
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
            maxLength={500}
          />
          <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0', textAlign: 'right' }}>
            {comment.length}/500
          </p>
        </div>

        {/* Submit Button */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              backgroundColor: 'white',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Skip
          </button>
          <button
            onClick={handleSubmit}
            disabled={rating === 0 || isSubmitting}
            style={{
              padding: '12px 24px',
              backgroundColor: rating === 0 ? '#d1d5db' : primaryColor,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: rating === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: isSubmitting ? 0.7 : 1
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>

      <div style={{ 
        marginTop: '16px', 
        padding: '12px', 
        backgroundColor: '#f3f4f6', 
        borderRadius: '8px',
        fontSize: '14px',
        color: '#6b7280',
        textAlign: 'center'
      }}>
        💡 Your feedback helps us improve our service
      </div>
    </div>
  )
}